/**
 * Keeping obstruction labels apart on the PLAN (owner, 2026-08-05).
 *
 * The plan has no height axis. A switch above an outlet is two distinct
 * fixtures on the elevation, but one footprint position on the plan — so their
 * labels land on the same point and print on top of each other.
 *
 * The fix is to fan them out along the inward normal: same position on the
 * wall, different distances into the room. This module decides WHICH LANE each
 * label takes; the canvas turns a lane into a distance, because only it knows
 * the wall's direction.
 */

/**
 * The narrowest along-wall span a label is treated as occupying, in units.
 * Roughly what a two-character code covers at a typical plan scale — enough
 * that two outlets a couple of inches apart are recognised as a collision.
 */
const MIN_LABEL_SPAN_UNITS = 4;

/**
 * Assigns each obstruction a label lane, 0 being closest to the wall.
 *
 * Lanes are the classic interval-colouring: walk the items in order and give
 * each the LOWEST lane not already taken by something its span overlaps. Two
 * fixtures that merely sit on the same wall, well apart, both keep lane 0 —
 * only genuine collisions cost distance, so a plan never gets pushed out more
 * than it has to.
 *
 * Order is by height, tallest first, so the **lowest fixture ends up furthest
 * from the wall** (owner: "perhaps the bottom obstruction is farther from the
 * wall"). That reads correctly against the elevation, where the eye travels
 * down the wall as the labels travel out from it. Ties break on id so the
 * result never depends on array order.
 *
 * Spans that merely TOUCH do not collide — abutting fixtures' labels are
 * already centred on different points.
 *
 * @param {Array<{id:string, offsetAlongWall?:number, widthUnits?:number,
 *   offsetFromFloor?:number}>} items obstructions on ONE wall
 * @param {number} [tol] slack, in units, for float arithmetic
 * @returns {Map<string, number>} id → lane index
 */
export function assignLabelLanes(items, tol = 0.01) {
  const list = (Array.isArray(items) ? items : []).filter((o) => o && o.id);
  // What collides is the LABEL, not the fixture: "O1" is wider than the 2¾"
  // outlet it names, and a zero-width or unmeasured fixture still needs room
  // for its code. So each span is at least MIN_LABEL_SPAN_UNITS wide, centred
  // on the fixture.
  const span = (o) => {
    const start = Number(o.offsetAlongWall);
    const width = Number(o.widthUnits);
    const s = Number.isFinite(start) ? start : 0;
    const w = Number.isFinite(width) && width > 0 ? width : 0;
    const centre = s + w / 2;
    const half = Math.max(w, MIN_LABEL_SPAN_UNITS) / 2;
    return { start: centre - half, end: centre + half };
  };
  const ordered = list.slice().sort((a, b) => {
    const ah = Number.isFinite(a.offsetFromFloor) ? a.offsetFromFloor : 0;
    const bh = Number.isFinite(b.offsetFromFloor) ? b.offsetFromFloor : 0;
    if (ah !== bh) return bh - ah; // tallest first → lowest ends up furthest out
    return String(a.id).localeCompare(String(b.id));
  });

  const placed = []; // { span, lane }
  const lanes = new Map();
  for (const item of ordered) {
    const mine = span(item);
    const taken = new Set(
      placed
        .filter((p) => !(p.span.end - mine.start <= tol || mine.end - p.span.start <= tol))
        .map((p) => p.lane),
    );
    let lane = 0;
    while (taken.has(lane)) lane += 1;
    lanes.set(item.id, lane);
    placed.push({ span: mine, lane });
  }
  return lanes;
}
